import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  LinearProgress,
  InputAdornment,
} from "@mui/material";
import ScienceIcon from "@mui/icons-material/Science";
import AddIcon from "@mui/icons-material/Add";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

const LabTrackerPage = () => {
  const [labResults, setLabResults] = useState([
    {
      id: 1,
      date: "2024-12-15",
      biomarker: "NT-proBNP",
      value: 1850,
      unit: "pg/mL",
      normalRange: { min: 0, max: 125 },
      status: "high",
      orderedBy: "Dr. Smith",
      notes: "Stable from last month",
    },
    {
      id: 2,
      date: "2024-12-15",
      biomarker: "Troponin T",
      value: 0.045,
      unit: "ng/mL",
      normalRange: { min: 0, max: 0.014 },
      status: "high",
      orderedBy: "Dr. Smith",
    },
    {
      id: 3,
      date: "2024-12-15",
      biomarker: "eGFR",
      value: 68,
      unit: "mL/min/1.73m²",
      normalRange: { min: 60, max: 120 },
      status: "normal",
      orderedBy: "Dr. Smith",
    },
    {
      id: 4,
      date: "2024-11-10",
      biomarker: "NT-proBNP",
      value: 2100,
      unit: "pg/mL",
      normalRange: { min: 0, max: 125 },
      status: "high",
      orderedBy: "Dr. Smith",
    },
    {
      id: 5,
      date: "2024-11-10",
      biomarker: "Free Light Chains (Kappa)",
      value: 245,
      unit: "mg/L",
      normalRange: { min: 3.3, max: 19.4 },
      status: "high",
      orderedBy: "Dr. Johnson",
    },
    {
      id: 6,
      date: "2024-10-05",
      biomarker: "NT-proBNP",
      value: 2350,
      unit: "pg/mL",
      normalRange: { min: 0, max: 125 },
      status: "high",
      orderedBy: "Dr. Smith",
    },
  ]);

  const [activeTab, setActiveTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [chartDialogOpen, setChartDialogOpen] = useState(false);
  const [selectedBiomarker, setSelectedBiomarker] = useState(null);

  const [formData, setFormData] = useState({
    date: "",
    biomarker: "",
    value: "",
    unit: "",
    normalMin: "",
    normalMax: "",
    orderedBy: "",
    notes: "",
  });

  const biomarkers = [
    {
      name: "NT-proBNP",
      fullName: "N-terminal pro-B-type natriuretic peptide",
      category: "Cardiac",
      unit: "pg/mL",
      normalRange: { min: 0, max: 125 },
      description: "Marker for heart failure and cardiac stress",
      icon: <MonitorHeartIcon />,
    },
    {
      name: "Troponin T",
      fullName: "Cardiac Troponin T",
      category: "Cardiac",
      unit: "ng/mL",
      normalRange: { min: 0, max: 0.014 },
      description: "Indicates cardiac muscle damage",
      icon: <MonitorHeartIcon />,
    },
    {
      name: "Troponin I",
      fullName: "Cardiac Troponin I",
      category: "Cardiac",
      unit: "ng/mL",
      normalRange: { min: 0, max: 0.04 },
      description: "Indicates cardiac muscle damage",
      icon: <MonitorHeartIcon />,
    },
    {
      name: "Free Light Chains (Kappa)",
      fullName: "Serum Free Kappa Light Chains",
      category: "AL Amyloidosis",
      unit: "mg/L",
      normalRange: { min: 3.3, max: 19.4 },
      description: "Marker for AL amyloidosis",
      icon: <WaterDropIcon />,
    },
    {
      name: "Free Light Chains (Lambda)",
      fullName: "Serum Free Lambda Light Chains",
      category: "AL Amyloidosis",
      unit: "mg/L",
      normalRange: { min: 5.7, max: 26.3 },
      description: "Marker for AL amyloidosis",
      icon: <WaterDropIcon />,
    },
    {
      name: "FLC Ratio",
      fullName: "Free Light Chain Ratio (Kappa/Lambda)",
      category: "AL Amyloidosis",
      unit: "ratio",
      normalRange: { min: 0.26, max: 1.65 },
      description: "Ratio of kappa to lambda light chains",
      icon: <WaterDropIcon />,
    },
    {
      name: "eGFR",
      fullName: "Estimated Glomerular Filtration Rate",
      category: "Renal",
      unit: "mL/min/1.73m²",
      normalRange: { min: 60, max: 120 },
      description: "Kidney function indicator",
      icon: <WaterDropIcon />,
    },
    {
      name: "Creatinine",
      fullName: "Serum Creatinine",
      category: "Renal",
      unit: "mg/dL",
      normalRange: { min: 0.7, max: 1.3 },
      description: "Kidney function marker",
      icon: <WaterDropIcon />,
    },
    {
      name: "Albumin",
      fullName: "Serum Albumin",
      category: "General",
      unit: "g/dL",
      normalRange: { min: 3.5, max: 5.5 },
      description: "Protein level in blood",
      icon: <ScienceIcon />,
    },
    {
      name: "Hemoglobin",
      fullName: "Hemoglobin",
      category: "General",
      unit: "g/dL",
      normalRange: { min: 12, max: 17 },
      description: "Red blood cell count",
      icon: <ScienceIcon />,
    },
  ];

  const handleDialogOpen = () => {
    setFormData({
      date: new Date().toISOString().split("T")[0],
      biomarker: "",
      value: "",
      unit: "",
      normalMin: "",
      normalMax: "",
      orderedBy: "",
      notes: "",
    });
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Auto-fill unit and normal range when biomarker is selected
    if (field === "biomarker") {
      const biomarker = biomarkers.find((b) => b.name === value);
      if (biomarker) {
        setFormData((prev) => ({
          ...prev,
          unit: biomarker.unit,
          normalMin: biomarker.normalRange.min,
          normalMax: biomarker.normalRange.max,
        }));
      }
    }
  };

  const handleAddResult = () => {
    if (!formData.date || !formData.biomarker || !formData.value) {
      alert("Please fill in required fields: Date, Biomarker, and Value");
      return;
    }

    const value = parseFloat(formData.value);
    const normalMin = parseFloat(formData.normalMin);
    const normalMax = parseFloat(formData.normalMax);

    let status = "normal";
    if (value < normalMin) {
      status = "low";
    } else if (value > normalMax) {
      status = "high";
    }

    const newResult = {
      id: Math.max(...labResults.map((r) => r.id), 0) + 1,
      date: formData.date,
      biomarker: formData.biomarker,
      value: value,
      unit: formData.unit,
      normalRange: { min: normalMin, max: normalMax },
      status: status,
      orderedBy: formData.orderedBy,
      notes: formData.notes,
    };

    setLabResults((prev) =>
      [...prev, newResult].sort((a, b) => new Date(b.date) - new Date(a.date))
    );

    handleDialogClose();
  };

  const handleDeleteResult = (id) => {
    if (window.confirm("Are you sure you want to delete this lab result?")) {
      setLabResults((prev) => prev.filter((r) => r.id !== id));
    }
  };

  const handleShowChart = (biomarkerName) => {
    setSelectedBiomarker(biomarkerName);
    setChartDialogOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "high":
        return "error";
      case "low":
        return "warning";
      case "normal":
        return "success";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "high":
        return <ErrorIcon />;
      case "low":
        return <WarningIcon />;
      case "normal":
        return <CheckCircleIcon />;
      default:
        return null;
    }
  };

  const getTrendIcon = (biomarkerName) => {
    const results = labResults
      .filter((r) => r.biomarker === biomarkerName)
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    if (results.length < 2) return <TrendingFlatIcon />;

    const latest = results[results.length - 1].value;
    const previous = results[results.length - 2].value;

    if (latest > previous * 1.1) return <TrendingUpIcon color="error" />;
    if (latest < previous * 0.9) return <TrendingDownIcon color="success" />;
    return <TrendingFlatIcon color="action" />;
  };

  const getChartData = (biomarkerName) => {
    return labResults
      .filter((r) => r.biomarker === biomarkerName)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map((r) => ({
        date: new Date(r.date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        value: r.value,
        normalMin: r.normalRange.min,
        normalMax: r.normalRange.max,
      }));
  };

  const getUniqueBiomarkers = () => {
    const unique = [...new Set(labResults.map((r) => r.biomarker))];
    return unique.map((name) => {
      const biomarker = biomarkers.find((b) => b.name === name);
      const results = labResults.filter((r) => r.biomarker === name);
      const latestResult = results.sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      )[0];

      return {
        name,
        category: biomarker?.category || "Other",
        latestValue: latestResult.value,
        unit: latestResult.unit,
        status: latestResult.status,
        date: latestResult.date,
        count: results.length,
      };
    });
  };

  const uniqueBiomarkers = getUniqueBiomarkers();
  const categories = ["All", "Cardiac", "AL Amyloidosis", "Renal", "General"];

  const filteredBiomarkers =
    activeTab === 0
      ? uniqueBiomarkers
      : uniqueBiomarkers.filter((b) => b.category === categories[activeTab]);

  const getAbnormalCount = () => {
    return uniqueBiomarkers.filter((b) => b.status !== "normal").length;
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <ScienceIcon sx={{ fontSize: 40, color: "primary.main" }} />
          <Typography variant="h3" fontWeight="bold">
            Lab Results Tracker
          </Typography>
          <Chip label="Premium" color="warning" icon={<CheckCircleIcon />} />
        </Box>
        <Typography variant="h6" color="text.secondary">
          Track important biomarkers and monitor trends over time
        </Typography>
      </Box>

      {/* Alert for Abnormal Results */}
      {getAbnormalCount() > 0 && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            <strong>
              {getAbnormalCount()} Biomarker(s) Outside Normal Range
            </strong>
          </Typography>
          <Typography variant="body2">
            Review your latest results and discuss with your healthcare
            provider.
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left Column - Stats */}
        <Grid item xs={12} md={3}>
          {/* Add Result Button */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={handleDialogOpen}
            sx={{
              background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
              py: 1.5,
              mb: 2,
            }}
          >
            Add Lab Result
          </Button>

          {/* Stats Cards */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <Card elevation={2}>
                <CardContent>
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    color="primary.main"
                  >
                    {uniqueBiomarkers.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Biomarkers Tracked
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h3" fontWeight="bold" color="error.main">
                    {getAbnormalCount()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Outside Normal Range
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card elevation={2}>
                <CardContent>
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    color="text.secondary"
                  >
                    {labResults.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Test Results
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quick Actions */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => alert("PDF upload coming soon!")}
            >
              Upload Lab PDF
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => alert("Export feature coming soon!")}
            >
              Export Results
            </Button>
          </Box>
        </Grid>

        {/* Right Column - Results */}
        <Grid item xs={12} md={9}>
          <Paper elevation={3}>
            {/* Tabs */}
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              {categories.map((cat, index) => (
                <Tab
                  key={cat}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {cat}
                      <Chip
                        label={
                          index === 0
                            ? uniqueBiomarkers.length
                            : uniqueBiomarkers.filter((b) => b.category === cat)
                                .length
                        }
                        size="small"
                      />
                    </Box>
                  }
                />
              ))}
            </Tabs>

            {/* Biomarkers Grid */}
            <Box sx={{ p: 3 }}>
              {filteredBiomarkers.length > 0 ? (
                <Grid container spacing={2}>
                  {filteredBiomarkers.map((biomarker) => {
                    const biomarkerInfo = biomarkers.find(
                      (b) => b.name === biomarker.name
                    );

                    return (
                      <Grid item xs={12} sm={6} md={4} key={biomarker.name}>
                        <Card
                          elevation={2}
                          sx={{
                            height: "100%",
                            transition: "all 0.3s",
                            "&:hover": {
                              transform: "translateY(-4px)",
                              boxShadow: 4,
                            },
                          }}
                        >
                          <CardContent>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "start",
                                mb: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                {biomarkerInfo?.icon}
                                <Typography
                                  variant="h6"
                                  fontWeight="bold"
                                  noWrap
                                >
                                  {biomarker.name}
                                </Typography>
                              </Box>
                              {getTrendIcon(biomarker.name)}
                            </Box>

                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="h4"
                                fontWeight="bold"
                                color="primary.main"
                              >
                                {biomarker.latestValue}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {biomarker.unit}
                              </Typography>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                              <Chip
                                label={biomarker.status.toUpperCase()}
                                color={getStatusColor(biomarker.status)}
                                icon={getStatusIcon(biomarker.status)}
                                size="small"
                                sx={{ mb: 1 }}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                Latest:{" "}
                                {new Date(biomarker.date).toLocaleDateString()}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {biomarker.count} test
                                {biomarker.count !== 1 ? "s" : ""} recorded
                              </Typography>
                            </Box>

                            <Button
                              fullWidth
                              variant="outlined"
                              size="small"
                              startIcon={<ShowChartIcon />}
                              onClick={() => handleShowChart(biomarker.name)}
                            >
                              View Trend
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <ScienceIcon
                    sx={{ fontSize: 80, color: "text.disabled", mb: 2 }}
                  />
                  <Typography variant="h5" color="text.secondary" gutterBottom>
                    No results found
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {activeTab === 0
                      ? "Start tracking your lab results"
                      : `No ${categories[activeTab]} biomarkers tracked yet`}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleDialogOpen}
                    sx={{
                      background:
                        "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
                    }}
                  >
                    Add Lab Result
                  </Button>
                </Box>
              )}
            </Box>

            {/* Recent Results Table */}
            {labResults.length > 0 && (
              <Box sx={{ p: 3, pt: 0 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Recent Results
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Date</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Biomarker</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Value</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Normal Range</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Status</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Actions</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {labResults.slice(0, 10).map((result) => (
                        <TableRow key={result.id} hover>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(result.date).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {result.biomarker}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {result.value} {result.unit}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {result.normalRange.min} -{" "}
                              {result.normalRange.max} {result.unit}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={result.status}
                              color={getStatusColor(result.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteResult(result.id)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Add Result Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Lab Result</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Test Date"
                type="date"
                required
                value={formData.date}
                onChange={(e) => handleInputChange("date", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Biomarker</InputLabel>
                <Select
                  value={formData.biomarker}
                  label="Biomarker"
                  onChange={(e) =>
                    handleInputChange("biomarker", e.target.value)
                  }
                >
                  {biomarkers.map((bio) => (
                    <MenuItem key={bio.name} value={bio.name}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {bio.icon}
                        {bio.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Value"
                type="number"
                required
                value={formData.value}
                onChange={(e) => handleInputChange("value", e.target.value)}
                InputProps={{
                  endAdornment: formData.unit && (
                    <InputAdornment position="end">
                      {formData.unit}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Unit"
                value={formData.unit}
                onChange={(e) => handleInputChange("unit", e.target.value)}
                placeholder="e.g., pg/mL"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Normal Range
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Minimum"
                type="number"
                value={formData.normalMin}
                onChange={(e) => handleInputChange("normalMin", e.target.value)}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Maximum"
                type="number"
                value={formData.normalMax}
                onChange={(e) => handleInputChange("normalMax", e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Ordered By"
                value={formData.orderedBy}
                onChange={(e) => handleInputChange("orderedBy", e.target.value)}
                placeholder="e.g., Dr. Smith"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                placeholder="Add any notes about this test..."
              />
            </Grid>

            {formData.biomarker && (
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="caption">
                    {
                      biomarkers.find((b) => b.name === formData.biomarker)
                        ?.description
                    }
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            onClick={handleAddResult}
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
            }}
          >
            Add Result
          </Button>
        </DialogActions>
      </Dialog>

      {/* Chart Dialog */}
      <Dialog
        open={chartDialogOpen}
        onClose={() => setChartDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ShowChartIcon color="primary" />
            {selectedBiomarker} Trend
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedBiomarker && (
            <Box sx={{ mt: 2 }}>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={getChartData(selectedBiomarker)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <ReferenceLine
                    y={getChartData(selectedBiomarker)[0]?.normalMax}
                    stroke="#ff9800"
                    strokeDasharray="3 3"
                    label="Max Normal"
                  />
                  <ReferenceLine
                    y={getChartData(selectedBiomarker)[0]?.normalMin}
                    stroke="#ff9800"
                    strokeDasharray="3 3"
                    label="Min Normal"
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#1A5EA8"
                    strokeWidth={3}
                    dot={{ r: 6 }}
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>

              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  The orange dashed lines represent the normal range for this
                  biomarker. Values outside this range should be discussed with
                  your healthcare provider.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChartDialogOpen(false)}>Close</Button>
          <Button
            onClick={() => alert("Download chart feature coming soon!")}
            startIcon={<DownloadIcon />}
          >
            Download Chart
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disclaimer */}
      <Paper elevation={1} sx={{ mt: 4, p: 3, bgcolor: "grey.50" }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Medical Disclaimer:</strong> This lab tracker is for
          informational and organizational purposes only. Abnormal lab values
          should always be reviewed with your healthcare provider. Normal ranges
          may vary by laboratory and individual patient factors. Do not make
          treatment decisions based solely on these results.
        </Typography>
      </Paper>
    </Container>
  );
};

export default LabTrackerPage;
